import { experimental_useObject as useObject } from "@ai-sdk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "@posthog/react/slim";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { DeepPartial } from "ai";
import { FileUser, MinusIcon, PlusIcon, Wand2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  improveResumeLlmOutput,
  type Aggressiveness,
  type ImproveResumeLlmOutput,
} from "@super-resume-new/resume-engine";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Checkbox } from "@super-resume-new/ui/components/checkbox";
import { Input } from "@super-resume-new/ui/components/input";
import { Label } from "@super-resume-new/ui/components/label";
import { Slider } from "@super-resume-new/ui/components/slider";
import { Tabs, TabsList, TabsTab } from "@super-resume-new/ui/components/tabs";
import { Textarea } from "@super-resume-new/ui/components/textarea";

import { Faq } from "@/components/faq";
import { FileUpload } from "@/components/file-upload";
import type { GapAnswer } from "@/components/gap-questionnaire";
import { SelectedJobCard } from "@/components/jobs/selected-job-card";
import { PageHero } from "@/components/page-hero";
import { PageLayout } from "@/components/page-layout";
import { ResumeLinkWarning } from "@/components/resume-link-warning";
import { ResumeResult } from "@/components/resume-result";
import { StreamProgress } from "@/components/stream-progress";
import { ToolSwitcher } from "@/components/tool-switcher";
import { useAuthModal } from "@/context/auth-modal";
import { getUser } from "@/functions/get-user";
import { client } from "@/utils/orpc";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/resume-tailor")({
  validateSearch: (search: Record<string, unknown>): { from_match?: string; job?: string } => ({
    from_match: typeof search.from_match === "string" ? search.from_match : undefined,
    job:
      typeof search.job === "string"
        ? search.job
        : typeof search.jobId === "string"
          ? (search.jobId as string)
          : undefined,
  }),
  beforeLoad: async () => {
    const session = await getUser();
    return { session };
  },
  head: () =>
    seo({
      title: "AI Resume Tailor for Any Job Description | JobCues",
      description:
        "Tailor your resume bullets to any job description in seconds with evidence-based ATS keyword redistribution, defend cards, and high ATS pass rates.",
      path: "/resume-tailor",
    }),
  component: TailorComponent,
});

type InputMode = "upload" | "paste";

const MODES: {
  value: Aggressiveness;
  label: string;
  coverage: string;
  desc: string;
}[] = [
  {
    value: "literal",
    label: "Light",
    coverage: "~50% coverage",
    desc: "Moves what you already wrote into scoring bullets. No new terms.",
  },
  {
    value: "balanced",
    label: "Balance",
    coverage: "70–80% coverage",
    desc: "Also adds what your stack provably implies — React means JavaScript, HTML, CSS.",
  },
  {
    value: "aggressive",
    label: "Aggressive",
    coverage: "90%+ coverage",
    desc: "Promotes skills-block and adjacent experience, with a defend card for every stretch.",
  },
];

function TailorComponent() {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const { session } = Route.useRouteContext();

  const [inputMode, setInputMode] = useState<InputMode>("upload");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [addToTracker, setAddToTracker] = useState(true);
  const [aggressiveness, setAggressiveness] = useState<Aggressiveness>("balanced");
  const modeIndex = MODES.findIndex((m) => m.value === aggressiveness);
  const mode = MODES[modeIndex]!;
  const [resultLevel, setResultLevel] = useState<Aggressiveness>("balanced");
  const gapAnswersRef = useRef("");
  const generationIdRef = useRef<string | null>(null);
  const [savedGenerationId, setSavedGenerationId] = useState<string | null>(null);

  const { data: userProfile } = useQuery({
    queryKey: ["user", "profile"],
    queryFn: async () => client.user.getProfile(),
    enabled: !!session?.user,
  });

  const hasMasterProfile = Boolean(userProfile?.masterResumeText?.trim());
  const hasProvidedResume = inputMode === "upload" ? !!resumeFile : resumeText.trim().length > 0;
  const showDefaultResumeNotice = !!session?.user && hasMasterProfile && !hasProvidedResume;

  const [finalResult, setFinalResult] = useState<ImproveResumeLlmOutput | null>(null);

  const {
    object: partial,
    isLoading: isStreaming,
    submit,
  } = useObject({
    api: "/api/improve-resume",
    schema: improveResumeLlmOutput,
    onFinish: async ({
      object,
      error,
    }: {
      object: DeepPartial<ImproveResumeLlmOutput> | undefined;
      error: Error | undefined;
    }) => {
      if (error) {
        toast.error("Failed to process AI response: " + error.message);
        return;
      }
      if (!object) {
        toast.error("The AI service did not respond. No credits were charged — please try again.");
        return;
      }
      if (object) {
        setFinalResult(object as ImproveResumeLlmOutput);
        // Only a tracked generation has a row to write edits back to.
        setSavedGenerationId(addToTracker ? generationIdRef.current : null);
        posthog.capture("resume_tailoring_completed", {
          added_to_tracker: addToTracker,
        });
        toast.success("Resume optimized! Pick a template and download.");
        queryClient.invalidateQueries({ queryKey: ["credits"] });
        if (addToTracker) {
          queryClient.invalidateQueries({ queryKey: ["tracker", "entries"] });
        }
      }
    },
    onError: (err: Error) => {
      if (err.message.includes("Insufficient credits")) {
        toast.error("Insufficient credits. Please purchase more credits to continue.");
      } else {
        toast.error("Generation failed: " + (err?.message ?? "Unknown error"));
      }
    },
    fetch: Object.assign(
      async (url: string | URL | Request, options?: RequestInit): Promise<Response> => {
        posthog.capture("resume_tailoring_started", {
          added_to_tracker: addToTracker,
          input_source:
            inputMode === "upload"
              ? "upload"
              : hasMasterProfile && !resumeText.trim()
                ? "master_profile"
                : "paste",
          rewrite_mode: aggressiveness,
        });
        const idempotencyKey = crypto.randomUUID();
        // Kept so a later hand-edit can find its tracker row — the server stores
        // this key as generation_id and the browser never learns the row id.
        generationIdRef.current = idempotencyKey;
        const extraInfo = [additionalInfo.trim(), gapAnswersRef.current].filter(Boolean).join("\n");
        if (inputMode === "upload" && resumeFile) {
          const form = new FormData();
          form.append("file", resumeFile);
          form.append("job_title", jobTitle);
          form.append("job_description", jobDescription);
          // Only link when the text is still the posting's own. If the user
          // edited it, that edit is the description and must be stored.
          if (selectedJob && jobDescription === selectedJob.rawText)
            form.append("job_slug", selectedJob.slug);
          form.append("add_to_tracker", String(addToTracker));
          form.append("aggressiveness", aggressiveness);
          if (extraInfo) form.append("additional_info", extraInfo);
          return fetch(url, {
            method: "POST",
            body: form,
            headers: { "X-Idempotency-Key": idempotencyKey },
          });
        }
        const body = JSON.parse((options?.body as string) ?? "{}") as Record<string, unknown>;
        return fetch(url, {
          ...options,
          headers: {
            ...options?.headers,
            "Content-Type": "application/json",
            "X-Idempotency-Key": idempotencyKey,
          },
          body: JSON.stringify({
            ...body,
            text: resumeText,
            job_title: jobTitle,
            job_description: jobDescription,
            ...(selectedJob && jobDescription === selectedJob.rawText
              ? { job_slug: selectedJob.slug }
              : {}),
            add_to_tracker: addToTracker,
            aggressiveness,
            ...(extraInfo ? { additional_info: extraInfo } : {}),
          }),
        });
      },
      { preconnect: () => {} },
    ) as typeof fetch,
  });

  const { openAuthModal } = useAuthModal();

  const handleGenerate = useCallback(() => {
    const hasResume =
      (inputMode === "upload" ? !!resumeFile : resumeText.trim().length > 0) || hasMasterProfile;
    if (!hasResume) {
      toast.error("Please provide your resume or set up your Master Career Profile in Account.");
      return;
    }
    if (!jobTitle.trim()) {
      toast.error("Please enter the job title.");
      return;
    }
    if (!jobDescription.trim()) {
      toast.error("Please enter the job description.");
      return;
    }

    if (!session?.user) {
      openAuthModal({
        defaultTab: "signup",
        title: "Sign Up to Tailor Your Resume",
        description: "Create your free account to optimize this resume with 3 free AI rewrites.",
        onSuccess: () => {
          setFinalResult(null);
          setResultLevel(aggressiveness);
          submit({ job_title: jobTitle, job_description: jobDescription });
        },
      });
      return;
    }

    setFinalResult(null);
    setResultLevel(aggressiveness);
    submit({ job_title: jobTitle, job_description: jobDescription });
  }, [
    inputMode,
    resumeFile,
    resumeText,
    hasMasterProfile,
    jobTitle,
    jobDescription,
    session?.user,
    openAuthModal,
    aggressiveness,
    submit,
  ]);

  const handleRerun = useCallback(
    (answers: GapAnswer[]) => {
      const lines = answers
        .filter((a) => a.answer.trim())
        .map((a) => `Q: ${a.question}\nA: ${a.answer.trim()}`)
        .join("\n");
      if (!lines) {
        toast.error("Answer at least one question first.");
        return;
      }
      gapAnswersRef.current = lines;
      handleGenerate();
    },
    [handleGenerate],
  );

  const handleReset = useCallback(() => {
    setFinalResult(null);
    gapAnswersRef.current = "";
    setResumeFile(null);
    setResumeText("");
    setJobTitle("");
    setJobDescription("");
    setAdditionalInfo("");
    setShowAdditionalInfo(false);
  }, []);

  const { from_match, job: jobSlugParam } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [selectedJob, setSelectedJob] = useState<{
    id: string;
    slug: string;
    title: string;
    company: string | null;
    workMode: string | null;
    locationPrimary: string | null;
    seniority: string | null;
    compMin: number | null;
    compMax: number | null;
    compCurrency: string | null;
    compPeriod: string | null;
    applyUrl?: string | null;
    rawText: string;
  } | null>(null);

  // Fetch job details if job slug/id query param is present
  const { data: jobResponse } = useQuery({
    queryKey: ["job", jobSlugParam],
    queryFn: async () => client.jobs.getBySlug({ slug: jobSlugParam! }),
    enabled: !!jobSlugParam,
  });

  useEffect(() => {
    if (jobResponse?.job) {
      setSelectedJob(jobResponse.job as any);
      setJobTitle(jobResponse.job.title);
      setJobDescription(jobResponse.job.rawText || "");
      toast.success(`Selected job: ${jobResponse.job.title}`);
    }
  }, [jobResponse]);

  const handleClearSelectedJob = () => {
    setSelectedJob(null);
    setJobTitle("");
    setJobDescription("");
    navigate({ search: (prev) => ({ ...prev, job: undefined }), replace: true });
  };

  useEffect(() => {
    if (!from_match) return;

    let mounted = true;
    client.jobs
      .getMatch({ matchId: from_match })
      .then((data) => {
        if (!mounted) return;
        if (data.resumeText) {
          setInputMode("paste");
          setResumeText(data.resumeText);
        }
        if (data.jobTitle) {
          setJobTitle(data.jobTitle);
        }
        if (data.jobDescription) {
          setJobDescription(data.jobDescription);
        }
        toast.success("Loaded resume and job details from your match check!");
        navigate({
          search: (prev) => ({
            ...prev,
            from_match: undefined,
            job: data.jobSlug || prev.job,
          }),
          replace: true,
        });
      })
      .catch((err) => {
        console.error("Failed to fetch match data:", err);
      });

    return () => {
      mounted = false;
    };
  }, [from_match, navigate]);

  const hasUnsavedChanges =
    !finalResult &&
    (!!resumeFile ||
      resumeText.trim().length > 0 ||
      jobTitle.trim().length > 0 ||
      jobDescription.trim().length > 0 ||
      additionalInfo.trim().length > 0);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const canGenerate =
    !isStreaming && jobTitle.trim().length > 0 && jobDescription.trim().length > 0;

  return (
    <PageLayout id="main-content" className="max-w-7xl">
      <PageHero
        eyebrow="Resume tailor"
        icon={Wand2}
        title={
          <>
            <strong>Get your resume</strong>
            <br />
            job-ready in seconds
          </>
        }
        description="Paste your resume and the target job, then export a rewritten, ATS-ready PDF."
      />

      {/* Tool Switcher */}
      <ToolSwitcher current="tailor" />

      {/* Form / Inputs Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: Job Description & Form Input */}
        <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground">Target Job</h2>
              {selectedJob && (
                <Badge
                  variant="secondary"
                  className="text-[10px] bg-brand/10 text-brand-foreground border-brand/20"
                >
                  Verified Job
                </Badge>
              )}
            </div>
            {!selectedJob && (
              <Link
                to="/jobs"
                className="text-xs font-semibold text-brand-foreground hover:underline flex items-center gap-1"
              >
                Browse Jobs Directory &rarr;
              </Link>
            )}
          </div>

          {selectedJob ? (
            <SelectedJobCard job={selectedJob} onClear={handleClearSelectedJob} />
          ) : (
            /* Manual Freeform Job Title & Description Inputs */
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="job-title" className="text-xs font-semibold text-muted-foreground">
                  Job Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="job-title"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Senior Full Stack Engineer"
                  disabled={isStreaming}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="job-desc" className="text-xs font-semibold text-muted-foreground">
                  Job Description <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="job-desc"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the full job description requirements, responsibilities, and qualifications…"
                  disabled={isStreaming}
                  rows={8}
                  className="resize-none text-xs leading-relaxed"
                />
              </div>
            </>
          )}

          {/* Aggressiveness Mode Slider */}
          <div className="rounded-xl border border-border bg-muted/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                Rewrite Mode: <span className="text-brand-foreground">{mode.label}</span>
              </Label>
              <span className="text-sm">{mode.coverage}</span>
            </div>
            <Slider
              value={[modeIndex]}
              onValueChange={(val) => {
                const idx = Array.isArray(val) ? val[0] : typeof val === "number" ? val : 1;
                if (idx !== undefined && MODES[idx]) {
                  setAggressiveness(MODES[idx].value);
                }
              }}
              min={0}
              max={2}
              step={1}
              disabled={isStreaming}
              className="my-3"
            />
            <p className="text-xs text-muted-foreground">{mode.desc}</p>
          </div>

          {/* Additional Info / Notes Toggle */}
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
              className="text-xs text-muted-foreground hover:text-foreground p-0 h-auto font-normal gap-1"
            >
              {showAdditionalInfo ? (
                <MinusIcon className="size-3" />
              ) : (
                <PlusIcon className="size-3" />
              )}
              {showAdditionalInfo ? "Hide additional context" : "Add extra notes / instructions"}
            </Button>

            {showAdditionalInfo && (
              <div className="mt-2">
                <Textarea
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  placeholder="Optional: Specify experiences to highlight, tone adjustments, or projects to emphasize…"
                  disabled={isStreaming}
                  rows={3}
                  className="text-xs"
                />
              </div>
            )}
          </div>

          {/* Auto-Add to Job Tracker Checkbox */}
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id="add-tracker"
              checked={addToTracker}
              onCheckedChange={(checked) => setAddToTracker(checked === true)}
              disabled={isStreaming}
            />
            <Label
              htmlFor="add-tracker"
              className="text-xs font-normal text-muted-foreground cursor-pointer"
            >
              Automatically create an entry in my{" "}
              <Link
                to="/job-tracker"
                className="font-semibold text-foreground underline underline-offset-2"
              >
                Application Tracker
              </Link>
            </Label>
          </div>

          {/* Submit Action */}
          <div className="mt-2 flex items-center gap-2">
            {hasUnsavedChanges && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleReset}
                disabled={isStreaming}
                className="cursor-pointer"
              >
                Reset
              </Button>
            )}
            <Button
              type="button"
              size="lg"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold gap-2 cursor-pointer shadow-sm"
            >
              <Wand2 className="size-4" />
              {isStreaming ? "Optimizing Resume…" : "Tailor Resume for Job"}
            </Button>
          </div>
        </div>

        {/* Right: Resume Input */}
        <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Your Resume</h2>
            <Tabs
              value={inputMode}
              onValueChange={(val) => setInputMode(val as InputMode)}
              className="w-auto"
            >
              <TabsList>
                <TabsTab value="upload" className="text-xs">
                  Upload PDF
                </TabsTab>
                <TabsTab value="paste" className="text-xs">
                  Paste Text
                </TabsTab>
              </TabsList>
            </Tabs>
          </div>

          {showDefaultResumeNotice && (
            <div className="flex gap-4 border-1 border-brand/30 bg-brand/5 p-3 rounded-lg">
              <div className="size-10 flex items-center justify-center bg-brand/10 text-brand-foreground p-2 rounded-md">
                <FileUser className="text-brand" />
              </div>
              <p className="text-sm">
                Using your{" "}
                <Link to="/account" className="font-semibold text-foreground text-brand-foreground">
                  Master Profile
                </Link>{" "}
                resume by default.
                <br></br>Upload or paste below to override it.
              </p>
            </div>
          )}

          {inputMode === "upload" ? (
            <FileUpload onFile={setResumeFile} disabled={isStreaming} />
          ) : (
            <Textarea
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Paste your full resume text here (experience, skills, education)…"
              disabled={isStreaming}
              rows={22}
              className="resize-none font-mono text-xs leading-relaxed"
            />
          )}

          {inputMode === "paste" && <ResumeLinkWarning text={resumeText} />}
        </div>
      </div>

      {/* Streaming Progress Indicator */}
      {isStreaming && (
        <div className="mt-8">
          <StreamProgress partial={partial} isStreaming={isStreaming} />
        </div>
      )}

      {/* Final Resume Result View */}
      {finalResult && (
        <div className="mt-10">
          <ResumeResult
            result={finalResult}
            aggressiveness={resultLevel}
            onRerun={handleRerun}
            rerunning={isStreaming}
            sourceText={resumeText}
            applyUrl={selectedJob?.applyUrl}
            company={selectedJob?.company}
            generationId={savedGenerationId}
          />
        </div>
      )}

      {/* Help / FAQ */}
      <div className="mt-16">
        <Faq />
      </div>
    </PageLayout>
  );
}
